/**
 * What to say about a custom domain, and when to keep showing the records.
 *
 * Extracted from the page for the same reason policy-form is: the decisions
 * are the part worth testing, and testing them through a React tree would
 * mean a testing library this app does not have in order to assert on
 * sentences it could assert on directly.
 *
 * THE DECISION THAT MATTERS
 *
 * `needsRecords`. Connecting a domain does not make it work - the records
 * are the customer's to create at their own DNS provider, and nothing we do
 * can create them. Somebody who closes the tab believing the job is done
 * has a domain that will never resolve. So the records stay on screen for
 * exactly as long as they are outstanding, and the one state that hides
 * them is the one where they have already worked.
 */

import type { CustomDomain } from './api.js'

/**
 * The status line, in the words somebody would use.
 *
 * Never the state name. "dns_applied" tells a person nothing; "waiting for
 * DNS to take effect" tells them both what is happening and that waiting is
 * normal, which is the difference between patience and a support ticket.
 */
export function explain(domain: Pick<CustomDomain, 'state' | 'failureReason'>): string {
  switch (domain.state) {
    case 'pending': return 'not started'
    case 'purchasing': return 'buying this domain'
    case 'connecting': return 'waiting for your DNS records'
    case 'dns_applied': return 'waiting for DNS to take effect'
    case 'verified': return 'DNS is correct — issuing the certificate'
    case 'issuing_ssl': return 'issuing the HTTPS certificate'
    case 'active': return 'certificate ready'
    // Never the bare word "failed". A failure with no reason attached is
    // still a failure somebody has to act on, and the action is always the
    // same one: compare the records against what is actually in their zone.
    case 'failed': return domain.failureReason ?? 'could not be connected — check the records below'
    case 'deleted': return 'removed'
    default: return domain.state
  }
}

/**
 * Which pill a state wears.
 *
 * Only `active` is green. Every intermediate state is amber, including the
 * ones that sound finished - `verified` means DNS resolves and the site
 * still does not, and a green pill there is the page telling somebody a
 * comfortable lie.
 */
export function pillClass(domain: Pick<CustomDomain, 'state'>): string {
  if (domain.state === 'active') return 'pill pill-active'
  if (domain.state === 'failed') return 'pill pill-dead'
  return 'pill pill-warn'
}

/**
 * Whether the DNS records still have to be shown.
 *
 * True for everything except a domain that is serving or gone. A failed
 * domain especially: the records are what failed, so hiding them at the
 * moment somebody needs to compare them character by character is the
 * worst possible time.
 */
export function needsRecords(domain: Pick<CustomDomain, 'state'>): boolean {
  return domain.state !== 'active' && domain.state !== 'deleted'
}

/** Whether "Check now" should be offered. Nothing to re-check once it is live. */
export function canCheck(domain: Pick<CustomDomain, 'state'>): boolean {
  return domain.state !== 'active' && domain.state !== 'deleted'
}
